// src/pages/EvaaSurveyPage.js
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom'; // 2. Import useParams
import SurveyDisplay from '../components/ui/SurveyDisplay'; // Adjust path if needed
import InvalidLinkPage from '../components/errors/invalidLinkPage';
import Spinner from '../components/ui/Spinner'; // Adjust path if needed
import { buildQuotaClosedHtml } from "../lib/utils"; // Correctly path to your new utility file

import API_CONFIG from '../config/api';
const BASE_URL = API_CONFIG.BASE_URL;

const getAttemptKey = (surveyId, participantId) => `evaa_attempt_${surveyId}_${participantId}`;

function EvaaSurveyPage() {
	// 3. Get the URL parameters. The key 'surveyId' MUST match the name in the Route path (:surveyId)
	const { surveyId, participantId } = useParams();
	const navigate = useNavigate(); // Initialize useNavigate hook
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [surveyModel, setSurveyModel] = useState({});

	const getSurveyForParticipant = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const savedAttempt = localStorage.getItem(getAttemptKey(surveyId, participantId));
			const attemptId = savedAttempt ? JSON.parse(savedAttempt).attempt_id : null;
			const endpoint = attemptId ? `${BASE_URL}/public/load-survey/${surveyId}/${participantId}?attempt_id=${attemptId}` : `${BASE_URL}/public/load-survey/${surveyId}/${participantId}`;


			const result = await fetch(endpoint, { 
				method: "GET", 
				headers: { "Content-Type": "application/json" } 
			});

			if (result.status === 404) {
				navigate('/404', { 
					state: { 
						message: `Survey with ID "${surveyId}" or participant "${participantId}" not found.`
					} 
				});
				return;
			}

			if (result.status === 401) {
				setError("You are not authorized to access this survey. Please check your invitation link.");
				setSurveyModel(null);
				return;
			}

			if (!result.ok) {
				let errorMessage = `Failed to fetch survey: Server responded with status ${result.status}.`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {
					// Response body isn't JSON? Ignore and keep generic error
				}
				console.error("EvaaSurveyPage API Error:", errorMessage, result);
				setError(errorMessage);
				setSurveyModel(null);
				return;
			}

			const data = await result.json();
			if (data) {
				setSurveyModel(data);
				localStorage.setItem(
					getAttemptKey(surveyId, participantId),
					JSON.stringify({
						survey_id: surveyId,
						participant_id: participantId,
						attempt_id: data.attempt_id
					})
				  );
			} else {
				console.error("API returned empty or invalid data structure:", data);
				setError("Survey data is empty or malformed.");
				setSurveyModel([]);
			}
		} catch (err) {
			console.error("Network or unexpected error while fetching survey:", err);
			setError("Failed to load survey. Please check your connection and try again.");
			setSurveyModel([]);
		} finally {
			setIsLoading(false);
		}
	}, [surveyId, participantId, navigate]); // Include all dependencies

	useEffect(() => {
		getSurveyForParticipant();
	}, [getSurveyForParticipant]); // The dependency array ensures this effect runs when surveyId changes
	
	
	
	const handleSurveySubmission = useCallback(async (surveyResults, surveyOptions) => {
		if (surveyOptions) {
			surveyOptions.showSaveInProgress();
		}
		try {
			const endpoint = `${BASE_URL}/public/save-survey-response/${surveyId}/${participantId}`
			const response = await fetch(endpoint, {
				method: "POST",
				body: JSON.stringify({
					survey_id: surveyId,
					response_id: surveyModel.attempt_id,
					survey_data: surveyResults.getPlainData()
				}),
				headers: { "Content-Type": "application/json" }
			});
	
			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
			}
	
			if (surveyOptions && surveyOptions.showSaveSuccess) {
				surveyOptions.showSaveSuccess();
			}
			localStorage.removeItem(getAttemptKey(surveyId, participantId));
		} catch (err) {
			console.error("Error submitting survey:", err);
	
			// Show SurveyJS's built-in "Error" message
			if (surveyOptions && surveyOptions.showSaveError) {
				// You can pass a custom message here to display within the survey
				surveyOptions.showSaveError("Failed to submit. Please check your connection and try again.");
			}
	
		}
	}, [surveyId, participantId, surveyModel?.attempt_id]);

	const handleSurveyHasStarted = useCallback(async () => {
		const endpoint = `${BASE_URL}/public/start-survey/${surveyId}/${participantId}/${surveyModel.attempt_id}`
		const response = await fetch(endpoint, { method: "GET" });
		if (!response.ok) {
			const errorData = await response.json();
			// TODO this might break the flow in case there is an error, it got to be tested in prod
			// on the other hand, if that one break, maybe the all flow will break ?? 
			throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
		}

	}, [surveyModel?.attempt_id]);

	const handleOnValidatePage = useCallback(async (surveyResults, surveyOptions) => {
		const endpoint = `${BASE_URL}/public/validate-quotas/${surveyId}/${participantId}`
		const response = await fetch(endpoint, {
			method: "POST",
			body: JSON.stringify({
					survey_id: surveyId,
					response_id: surveyModel.attempt_id,
					survey_data: surveyResults.getPlainData()
			}),
			headers: { "Content-Type": "application/json" }
		})

		if (response.status === 404) {
			navigate('/404', { 
				state: { 
					message: `Survey with ID "${surveyId}" or participant "${participantId}" not found.`
				} 
			});
			return;
		}

		if (!response.ok) {
			console.error("DEBUG error quotas check !!!! ->", response.status)
			const errorData = await response.json();
			const message = errorData.detail || `HTTP error! status: ${response.status}`
			navigate('/errors/evaa/oops/fivehundred',{ state: { 
				message: message
			}})
			return
		}

		const data = await response.json();
		if (data && data.accepted) {
			// if data is validated you want to continue normal execution
			return
		} else if (data && !data.accepted) {
			// TODO a more dynamic way to choose the completedHtmlOnCondition ased on server response
			const completedHtmlOnCondition = surveyResults.completedHtmlOnCondition
			if (Array.isArray(completedHtmlOnCondition) && completedHtmlOnCondition.length > 0) {
				surveyResults.completedHtml = completedHtmlOnCondition[0].html
			} else {
				surveyResults.completedHtml = buildQuotaClosedHtml(data);
			}
			surveyResults.doComplete();
			return
		} else {
			console.error("API returned empty or invalid data structure:", data);
			setError("Data validation for quotas is empty or malformed");
			setSurveyModel([]);
		}


	}, [surveyId, participantId, surveyModel?.attempt_id]);

	if (isLoading) {
		return (
			<div className="flex flex-col items-center justify-center min-h-screen gap-2">
				<Spinner text="Loading your survey..." size="md" className="mb-4" />
			</div>
		);
	}

	if (error) {
		return <InvalidLinkPage />;
	}

	return (
			<div style={{ padding: '10px' }}>
					{/* The Survey component will now render with the updated title including the ID */}
					<SurveyDisplay surveyJson={surveyModel.survey}
					surveyThemeJson={surveyModel.survey_theme}
					onSurveyComplete={handleSurveySubmission}
					onSurveyHasStarted={handleSurveyHasStarted}
					onPageValidation={handleOnValidatePage}
					/>
			</div>
	);
}

export default EvaaSurveyPage;